import {
  getQuoteSupplierFormat,
  isSupportedQuoteSupplierFormat,
  resolveQuoteSupplierFormat,
  SUPPORTED_QUOTE_FORMAT_IDS,
} from '../config/quoteSupplierFormats';
import { logInfo, logWarn } from '../config_and_helpers/logger';

const LOG_CONTEXT = 'quoteSupplierFormatValidation';

/**
 * @typedef {Object} SupplierFormatContextResult
 * @property {boolean} canProceed
 * @property {string[]} blockingErrors
 * @property {import('../types/quoteExtractionTypes').ExtractionWarning[]} warnings
 * @property {{ formatId: string, displayName: string }|null} format
 * @property {{ value: unknown, label: string, matchReason: string }|null} suggestedSupplier
 */

/**
 * Validate extraction supplier format and suggest Materiah supplier by name match.
 *
 * @param {import('../types/quoteExtractionTypes').ExtractedQuoteData} extractedData
 * @param {Array<{ value: unknown, label?: string }>} [supplierOptions]
 * @returns {SupplierFormatContextResult}
 */
export function validateExtractedQuoteSupplierContext(
  extractedData,
  supplierOptions = []
) {
  const blockingErrors = [];
  const warnings = [];
  const { format, formatId } = resolveQuoteSupplierFormat(extractedData);

  if (!formatId) {
    blockingErrors.push(
      'Could not identify the quote format. The PDF may be from an unsupported supplier.'
    );
    logWarn(LOG_CONTEXT, 'missing supplierFormat in extraction meta', {});
    return {
      canProceed: false,
      blockingErrors,
      warnings,
      format: null,
      suggestedSupplier: null,
    };
  }

  if (!format || !format.supported) {
    blockingErrors.push(
      `Unsupported supplier quote format (“${formatId}”). Contact an administrator to enable this supplier.`
    );
    logWarn(LOG_CONTEXT, 'unsupported supplierFormat', { formatId });
    return {
      canProceed: false,
      blockingErrors,
      warnings,
      format: null,
      suggestedSupplier: null,
    };
  }

  if (!SUPPORTED_QUOTE_FORMAT_IDS.includes(format.formatId)) {
    blockingErrors.push(`Quote format “${formatId}” is not enabled in this application.`);
    return {
      canProceed: false,
      blockingErrors,
      warnings,
      format: null,
      suggestedSupplier: null,
    };
  }

  const extractedSupplier = extractedData?.supplier || {};
  if (!format.verifyFingerprints(extractedSupplier)) {
    warnings.push({
      field: 'meta.supplierFormat',
      message: `Extracted supplier details do not fully match ${format.displayName}. Please verify before saving.`,
      severity: 'warning',
    });
    logWarn(LOG_CONTEXT, 'fingerprint mismatch', {
      formatId: format.formatId,
      supplierName: extractedSupplier.name ?? null,
    });
  }

  const match = format.matchMateriahSupplierOption(
    supplierOptions,
    extractedSupplier
  );

  if (match) {
    logInfo(LOG_CONTEXT, 'Materiah supplier matched', {
      formatId: format.formatId,
      supplierId: match.value,
      matchReason: match.matchReason,
      score: match.score,
    });
  } else if (format.formatId !== 'rhenium') {
    warnings.push({
      field: 'supplier',
      message: `No Materiah supplier matched the ${format.displayName} name on this quote. Select the supplier manually.`,
      severity: 'warning',
    });
    logWarn(LOG_CONTEXT, 'no Materiah supplier match', {
      formatId: format.formatId,
      extractedName: extractedSupplier.name ?? null,
    });
  }

  return {
    canProceed: true,
    blockingErrors,
    warnings,
    format: { formatId: format.formatId, displayName: format.displayName },
    suggestedSupplier: match
      ? {
          value: match.value,
          label: match.label,
          matchReason: match.matchReason,
        }
      : null,
  };
}

export { getQuoteSupplierFormat, isSupportedQuoteSupplierFormat, resolveQuoteSupplierFormat };
